import { useParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { getTerraReply } from "../api/gemini";
import ChatBubble from "../components/ChatBubble";

function ChatPage() {
  const { eco } = useParams(); // e.g., rainforest, ocean, glacier
  const displayName = eco.charAt(0).toUpperCase() + eco.slice(1);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    setMessages([]);
  }, [eco]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMsg = { sender: "user", text: input };
    const updatedMessages = [...messages, userMsg];

    setMessages(updatedMessages);
    setInput("");
    setLoading(true);

    try {
      const reply = await getTerraReply(input, displayName, updatedMessages);
      const aiMsg = { sender: "ai", text: reply };
      setMessages((prev) => [...prev, aiMsg]);
    } catch (error) {
      console.error("❌ Chat error:", error);
      setMessages((prev) => [
        ...prev,
        { sender: "ai", text: "⚠️ Terra couldn’t speak right now." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center px-4 py-6 bg-cover bg-center"
      style={{
        backgroundImage:
          "url('https://img.freepik.com/premium-photo/beautiful-8k-nature-scene-background-wallpaper_1287271-3853.jpg')",
        backgroundAttachment: "fixed",
        backgroundColor: "#dbeafe" // Fallback
      }}
    >
      {/* Header */}
      <div className="w-full max-w-4xl text-center mb-4">
        <h1 className="text-3xl font-bold text-green-800 mb-1">
          🌿 Chat with the {displayName}
        </h1>
        <p className="text-gray-100 text-sm italic backdrop-blur-sm px-3 py-1 rounded bg-black/30 inline-block">
          Feel the Earth speak — poetic, personal, and powerful.
        </p>
      </div>

      {/* Chat Messages */}
      <div className="w-full max-w-4xl bg-white/70 backdrop-blur-md border border-green-200 rounded-2xl shadow-lg p-4 h-[60vh] sm:h-[65vh] overflow-y-auto custom-scrollbar">
        {messages.map((msg, i) => (
          <ChatBubble key={i} text={msg.text} sender={msg.sender} />
        ))}
        {loading && (
          <ChatBubble
            text={`🌱 ${displayName} is thinking...`}
            sender="ai"
          />
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex w-full max-w-4xl gap-2 mt-4">
        <input
          type="text"
          aria-label="Ask a question"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          className="flex-1 p-3 rounded-full border border-green-400 focus:outline-none focus:ring-2 focus:ring-green-300 text-sm bg-white/80 backdrop-blur-sm"
          placeholder={`Ask the ${displayName} something...`}
        />
        <button
          type="button"
          aria-label="Send message"
          onClick={sendMessage}
          disabled={loading}
          className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-full text-sm font-medium transition disabled:opacity-80"
        >
          Send
        </button>
      </div>

      {/* Back Button */}
      <button
        onClick={() => window.history.back()}
        className="mt-6 text-sm text-white hover:underline bg-black/30 px-4 py-1 rounded backdrop-blur"
      >
        ← Back to Ecosystem Select
      </button>
    </div>
  );
}

export default ChatPage;
